/**
 * Festival picker — Step 0 for the festival wizard mode.
 *
 * Replaces the generic BrandPicker for the festival flow because a
 * festival isn't a business — it's a stacked monetization product
 * with multiple sponsorship tiers we sell separately.
 *
 * Flow
 * ─────
 *   1. Show every active festival as a card (hero image, dates,
 *      countdown, "X of Y slots filled" thermometer).
 *   2. Operator picks one → reveals tier selector inline.
 *   3. Operator picks a tier → if tier is "stage" and the festival
 *      has sub-events, reveal sub-event selector.
 *   4. Confirm → store.selectFestivalAndTier() pre-fills the rest of
 *      the wizard and advances to the brand picker (step 1).
 *
 * Why everything happens in one step
 * ──────────────────────────────────
 * Sales people think in terms of "which slot at which festival"
 * as a single decision. Splitting festival and tier into two
 * separate steps would feel mechanical. The single-step pattern
 * also keeps the festival flow at 6 steps total — same count as
 * the sponsor flow — so the step pills don't reflow.
 */

import { useEffect, useMemo, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import {
  Loader2,
  CalendarDays,
  MapPin,
  Sparkles,
  ArrowLeft,
  PartyPopper,
  Check,
} from 'lucide-react';
import { useWizardStore, type FestivalEntry, type FestivalTierKey } from '@/stores/campaignWizardStore';

// ── Tier visual map ───────────────────────────────────────────────────────
// Each tier has a label, helper copy, and a color. Used both on the
// cards and on the live preview pane upstream.

const TIER_VISUAL: Record<
  FestivalTierKey,
  { label: string; emoji: string; helper: string; accent: string }
> = {
  title: {
    label: 'Title sponsor',
    emoji: '👑',
    helper: 'Full festival branding, launch splash, every-surface logo',
    accent: '#a855f7',
  },
  stage: {
    label: 'Stage / sub-event sponsor',
    emoji: '🎤',
    helper: 'One sub-event takeover (Calypso Monarch, J\'Ouvert, etc.)',
    accent: '#ec4899',
  },
  corridor: {
    label: 'Corridor sponsor',
    emoji: '🛣️',
    helper: 'Festival-day banner on a specific transit corridor',
    accent: '#0ea5e9',
  },
  venue_partner: {
    label: 'Venue partner',
    emoji: '📍',
    helper: 'Featured map pin near the venue during the festival window',
    accent: '#10b981',
  },
  after_party: {
    label: 'After-party sponsor',
    emoji: '🌙',
    helper: 'Late-night ride-complete takeover for festival-goers heading home',
    accent: '#f59e0b',
  },
  sub_event: {
    label: 'Sub-event sponsor',
    emoji: '🎪',
    helper: 'Same as stage tier (alias for legacy data)',
    accent: '#ec4899',
  },
};

const TIER_ORDER: FestivalTierKey[] = [
  'title',
  'stage',
  'corridor',
  'venue_partner',
  'after_party',
];

// ── Helpers ───────────────────────────────────────────────────────────────

function fmtDateRange(start: string, end: string): string {
  const s = new Date(start);
  const e = new Date(end);
  const fmt: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  const sameMonth = s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear();
  if (sameMonth) {
    return `${s.toLocaleDateString('en-US', fmt)} – ${e.getDate()}, ${e.getFullYear()}`;
  }
  return `${s.toLocaleDateString('en-US', fmt)} – ${e.toLocaleDateString('en-US', { ...fmt, year: 'numeric' })}`;
}

function fmtCountdown(daysUntilStart: number): { label: string; tone: 'imminent' | 'upcoming' | 'live' | 'ended' } {
  if (daysUntilStart < -30) return { label: 'Ended', tone: 'ended' };
  if (daysUntilStart < 0) return { label: 'Live now', tone: 'live' };
  if (daysUntilStart === 0) return { label: 'Today', tone: 'live' };
  if (daysUntilStart <= 7) return { label: `In ${daysUntilStart} day${daysUntilStart === 1 ? '' : 's'}`, tone: 'imminent' };
  return { label: `In ${daysUntilStart} days`, tone: 'upcoming' };
}

// ── Component ─────────────────────────────────────────────────────────────

export default function FestivalPicker() {
  const store = useWizardStore('festival');
  const { selectedFestival, festivalTier, festivalSubEventSlug } = store;

  const [festivals, setFestivals] = useState<FestivalEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: rpcErr } = await supabase.rpc('admin_list_festivals', {
        p_horizon_days: 365,
        p_include_past: false,
      });
      if (rpcErr) throw rpcErr;
      const env = data as { ok?: boolean; festivals?: FestivalEntry[]; error?: string };
      if (env?.ok === false) throw new Error(env.error || 'forbidden');
      setFestivals(env?.festivals ?? []);
    } catch (err: any) {
      setError(err.message || 'Failed to load festivals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Phase 1: festival selection. When the user picks a festival, we
  // stage it in local state without advancing the wizard yet — they
  // still need to pick the tier.
  const [stagedFestival, setStagedFestival] = useState<FestivalEntry | null>(
    selectedFestival,
  );
  const [stagedTier, setStagedTier] = useState<FestivalTierKey | null>(festivalTier);
  const [stagedSubEvent, setStagedSubEvent] = useState<string | null>(
    festivalSubEventSlug,
  );

  // Tiers actually offered for the staged festival (some festivals
  // don't sell every tier — e.g. small events without an after-party).
  const offeredTiers = useMemo(() => {
    if (!stagedFestival) return [];
    return TIER_ORDER.filter((t) =>
      stagedFestival.sponsorship_tiers.some((cfg) => cfg.tier === t),
    );
  }, [stagedFestival]);

  // Whether the current selection is complete enough to commit
  const selectionComplete = useMemo(() => {
    if (!stagedFestival || !stagedTier) return false;
    // Stage tier with sub-events available requires a sub-event pick.
    const isStage = stagedTier === 'stage' || stagedTier === 'sub_event';
    if (isStage && stagedFestival.sub_events.length > 0 && !stagedSubEvent) {
      return false;
    }
    return true;
  }, [stagedFestival, stagedTier, stagedSubEvent]);

  const commit = () => {
    if (!stagedFestival || !stagedTier) return;
    store.selectFestivalAndTier(stagedFestival, stagedTier, stagedSubEvent);
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-400 mr-2" />
        <span className="text-sm text-gray-400">Loading Dominica festivals…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5">
        <p className="text-sm text-rose-200">Failed to load festivals: {error}</p>
        <p className="text-xs text-rose-200/70 mt-2">
          Apply migration{' '}
          <code className="font-mono text-cyan-300">20260620640000_festivals_first_class</code>
          {' '}and{' '}
          <code className="font-mono text-cyan-300">20260620650000_seed_dominica_festivals</code>.
        </p>
        <Button onClick={load} size="sm" className="mt-3">Retry</Button>
      </div>
    );
  }

  if (festivals.length === 0) {
    return (
      <div className="text-center py-16 text-gray-400">
        <PartyPopper className="w-8 h-8 text-gray-600 mx-auto mb-3" />
        <p className="text-sm">No festivals in the next 12 months.</p>
        <p className="text-xs text-gray-500 mt-1">
          Add one from the Festivals admin page.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div>
        <h2 className="text-lg font-bold text-white">Pick a festival to sponsor</h2>
        <p className="text-sm text-gray-400 mt-0.5">
          Each festival has multiple sponsorship slots you can sell separately.
        </p>
      </div>

      {/* Step 1 — Festival cards */}
      {!stagedFestival && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {festivals.map((f) => (
            <FestivalCard
              key={f.id}
              festival={f}
              onSelect={() => {
                setStagedFestival(f);
                setStagedTier(null);
                setStagedSubEvent(null);
              }}
            />
          ))}
        </div>
      )}

      {/* Step 2 — Tier picker (revealed after a festival is staged) */}
      {stagedFestival && (
        <div className="space-y-4">
          {/* Selected festival summary + back */}
          <div
            className="rounded-xl p-4 flex items-center gap-4 border"
            style={{
              borderColor: `${stagedFestival.theme_color}55`,
              background: `linear-gradient(120deg, ${stagedFestival.theme_color}22, transparent 70%)`,
            }}
          >
            <button
              type="button"
              onClick={() => {
                setStagedFestival(null);
                setStagedTier(null);
                setStagedSubEvent(null);
              }}
              className="p-2 rounded-md text-gray-400 hover:text-white hover:bg-white/5"
              aria-label="Pick a different festival"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="text-2xl">{stagedFestival.emoji ?? '🎉'}</div>
            <div className="flex-1 min-w-0">
              <div className="text-white font-semibold truncate">{stagedFestival.name}</div>
              <div className="text-xs text-gray-400 flex items-center gap-3 mt-0.5">
                <span className="inline-flex items-center gap-1">
                  <CalendarDays className="w-3 h-3" />
                  {fmtDateRange(stagedFestival.starts_at, stagedFestival.ends_at)}
                </span>
                {stagedFestival.venue_name && (
                  <span className="inline-flex items-center gap-1 truncate">
                    <MapPin className="w-3 h-3" />
                    {stagedFestival.venue_name}
                  </span>
                )}
              </div>
            </div>
          </div>

          <div>
            <h3 className="text-sm font-semibold text-white mb-2">
              Pick the sponsorship slot
            </h3>
            <p className="text-xs text-gray-500 mb-3">
              Each tier maps to a different campaign type and pricing band. Multiple
              sponsors can buy different tiers on the same festival.
            </p>

            <div className="space-y-2">
              {offeredTiers.map((tier) => {
                const visual = TIER_VISUAL[tier];
                const cfg = stagedFestival.sponsorship_tiers.find((t) => t.tier === tier);
                if (!cfg) return null;
                const filled =
                  stagedFestival.sponsorships?.filter((s) => s.tier === tier).length ?? 0;
                const available = Math.max(0, cfg.slot_count - filled);
                const isSelected = stagedTier === tier;
                return (
                  <button
                    type="button"
                    key={tier}
                    onClick={() => {
                      setStagedTier(tier);
                      setStagedSubEvent(null);
                    }}
                    className={`w-full text-left rounded-xl border p-4 transition-all flex items-start gap-3 ${
                      isSelected
                        ? 'bg-white/[0.06]'
                        : 'bg-gray-900/40 hover:bg-white/[0.04]'
                    }`}
                    style={{
                      borderColor: isSelected ? visual.accent : 'rgba(255,255,255,0.08)',
                      boxShadow: isSelected ? `inset 0 0 0 1px ${visual.accent}` : undefined,
                    }}
                  >
                    <div className="text-2xl shrink-0">{visual.emoji}</div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="text-white font-semibold">{visual.label}</div>
                        <div className="flex items-center gap-2 text-xs">
                          <span
                            className="px-2 py-0.5 rounded-full font-mono"
                            style={{
                              background: `${visual.accent}22`,
                              color: visual.accent,
                              border: `1px solid ${visual.accent}55`,
                            }}
                          >
                            EC${cfg.rate_xcd_min.toLocaleString()}–{cfg.rate_xcd_max.toLocaleString()}
                          </span>
                          <span
                            className={`px-2 py-0.5 rounded-full ${
                              available === 0
                                ? 'bg-gray-800 text-gray-500'
                                : 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                            }`}
                          >
                            {available === 0
                              ? 'Sold out'
                              : `${available} of ${cfg.slot_count} open`}
                          </span>
                        </div>
                      </div>
                      <p className="text-xs text-gray-400 mt-1">{visual.helper}</p>
                      {cfg.perks?.length > 0 && (
                        <ul className="mt-2 text-[11px] text-gray-500 space-y-0.5">
                          {cfg.perks.slice(0, 3).map((perk, i) => (
                            <li key={i} className="flex items-center gap-1.5">
                              <Sparkles className="w-2.5 h-2.5 text-cyan-400 shrink-0" />
                              {perk}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                    {isSelected && (
                      <Check
                        className="w-5 h-5 shrink-0"
                        style={{ color: visual.accent }}
                      />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Sub-event selector (only for stage tier on festivals with sub-events) */}
          {stagedTier === 'stage' &&
            stagedFestival.sub_events.length > 0 && (
              <div>
                <h3 className="text-sm font-semibold text-white mb-2">
                  Pick the sub-event
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  The campaign will run only during this sub-event's time window.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {stagedFestival.sub_events.map((se) => {
                    const isSel = stagedSubEvent === se.slug;
                    return (
                      <button
                        type="button"
                        key={se.slug}
                        onClick={() => setStagedSubEvent(se.slug)}
                        className={`text-left rounded-lg border px-3 py-2 transition-all ${
                          isSel
                            ? 'bg-white/[0.06] border-pink-400'
                            : 'bg-gray-900/40 border-white/8 hover:bg-white/[0.04]'
                        }`}
                      >
                        <div className="text-sm text-white font-medium">{se.name}</div>
                        <div className="text-[11px] text-gray-500 mt-0.5">
                          {fmtDateRange(se.starts_at, se.ends_at)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

          {/* Confirm */}
          <div className="pt-2 flex justify-end">
            <Button
              onClick={commit}
              disabled={!selectionComplete}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
            >
              Continue → pick sponsoring brand
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Festival card component ───────────────────────────────────────────────

interface FestivalCardProps {
  festival: FestivalEntry;
  onSelect: () => void;
}

function FestivalCard({ festival, onSelect }: FestivalCardProps) {
  const countdown = fmtCountdown(festival.days_until_start ?? 999);
  const filled = festival.revenue?.slot_count_filled ?? 0;
  const total = festival.revenue?.slot_count_total ?? 0;
  const fillPct = total > 0 ? Math.min(100, (filled / total) * 100) : 0;
  const booked = festival.revenue?.booked_xcd ?? 0;

  const tones: Record<typeof countdown.tone, { bg: string; text: string }> = {
    live: { bg: 'bg-emerald-500/15 border border-emerald-500/30', text: 'text-emerald-300' },
    imminent: { bg: 'bg-amber-500/15 border border-amber-500/30', text: 'text-amber-300' },
    upcoming: { bg: 'bg-cyan-500/10 border border-cyan-500/25', text: 'text-cyan-300' },
    ended: { bg: 'bg-gray-700/40 border border-gray-700', text: 'text-gray-400' },
  };

  return (
    <button
      type="button"
      onClick={onSelect}
      className="text-left rounded-xl overflow-hidden bg-gray-900/40 border border-gray-800 hover:border-white/15 hover:bg-white/[0.03] transition-all group"
    >
      {/* Hero strip */}
      <div
        className="h-20 relative overflow-hidden"
        style={{
          background: festival.hero_image_url
            ? `linear-gradient(180deg, transparent, rgba(0,0,0,0.6)), url(${festival.hero_image_url}) center/cover`
            : `linear-gradient(120deg, ${festival.theme_color}, ${festival.theme_color}99)`,
        }}
      >
        <div className="absolute top-2 left-2 text-2xl">{festival.emoji ?? '🎉'}</div>
        <div
          className={`absolute top-2 right-2 text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full ${tones[countdown.tone].bg} ${tones[countdown.tone].text}`}
        >
          {countdown.label}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <h3 className="text-white font-semibold leading-tight">{festival.name}</h3>
        <div className="text-xs text-gray-400 mt-1 flex items-center gap-1">
          <CalendarDays className="w-3 h-3" />
          {fmtDateRange(festival.starts_at, festival.ends_at)}
        </div>
        {festival.venue_name && (
          <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-1 truncate">
            <MapPin className="w-3 h-3" /> {festival.venue_name}
          </div>
        )}

        {/* Slot thermometer */}
        <div className="mt-3">
          <div className="flex items-center justify-between text-[10px] text-gray-500 uppercase tracking-wider">
            <span>Sponsorship slots</span>
            <span className="text-gray-300">
              {filled} of {total} filled
            </span>
          </div>
          <div className="mt-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
            <div
              className="h-full rounded-full"
              style={{
                width: `${fillPct}%`,
                background: `linear-gradient(90deg, ${festival.theme_color}, #ffffff44)`,
              }}
            />
          </div>
          {booked > 0 && (
            <div className="text-[11px] text-emerald-400 mt-1 font-mono">
              EC${booked.toLocaleString()} booked
            </div>
          )}
        </div>
      </div>
    </button>
  );
}
